import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FolderPlus,
  FilePlus,
  ChevronRight,
  Folder,
  FolderOpen,
  FileText,
  Trash2,
  MoreHorizontal,
  Pencil,
  MoveRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  CollaborativeEditor,
  CollaborativeEditorRef,
} from "@/components/editors/CollaborativeEditor";
import {
  GlobalDocument,
  useGlobalDocuments,
  useCreateGlobalDocument,
  useUpdateGlobalDocument,
  useDeleteGlobalDocument,
} from "@/hooks/useGlobalDocuments";

interface TreeNode extends GlobalDocument {
  children: TreeNode[];
}

function buildTree(items: GlobalDocument[]): TreeNode[] {
  const map: Record<string, TreeNode> = {};
  const roots: TreeNode[] = [];

  for (const item of items) {
    map[item.id] = { ...item, children: [] };
  }

  for (const item of items) {
    const node = map[item.id];
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort: folders first, then by sort_order, then by title
  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.is_folder !== b.is_folder) return a.is_folder ? -1 : 1;
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.title.localeCompare(b.title);
    });
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);

  return roots;
}

/** Check if `targetId` is a descendant of `nodeId` */
function isDescendant(
  docs: GlobalDocument[],
  nodeId: string,
  targetId: string,
): boolean {
  let current = docs.find((d) => d.id === targetId);
  while (current) {
    if (current.id === nodeId) return true;
    if (!current.parent_id) return false;
    current = docs.find((d) => d.id === current!.parent_id);
  }
  return false;
}

// ── Move-to dialog (for mobile / context menu) ──

function MoveToDialog({
  open,
  onOpenChange,
  docs,
  nodeId,
  onMove,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  docs: GlobalDocument[];
  nodeId: string;
  onMove: (nodeId: string, newParentId: string | null) => void;
}) {
  const folders = useMemo(
    () =>
      docs.filter(
        (d) =>
          d.is_folder && d.id !== nodeId && !isDescendant(docs, nodeId, d.id),
      ),
    [docs, nodeId],
  );

  const currentParentId = docs.find((d) => d.id === nodeId)?.parent_id ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>Move to...</DialogTitle>
        </DialogHeader>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          <button
            className={cn(
              "w-full text-left text-sm px-3 py-2 rounded-md hover:bg-accent flex items-center gap-2",
              currentParentId === null && "bg-accent",
            )}
            onClick={() => {
              onMove(nodeId, null);
              onOpenChange(false);
            }}
          >
            <FileText className="h-4 w-4 text-muted-foreground" />
            Root
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              className={cn(
                "w-full text-left text-sm px-3 py-2 rounded-md hover:bg-accent flex items-center gap-2",
                currentParentId === f.id && "bg-accent",
              )}
              onClick={() => {
                onMove(nodeId, f.id);
                onOpenChange(false);
              }}
            >
              <Folder className="h-4 w-4 text-muted-foreground" />
              {f.title}
            </button>
          ))}
          {folders.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">
              No folders available
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Tree Item ──

function TreeItem({
  node,
  level,
  selectedId,
  selectedFolderId,
  onSelect,
  expandedFolders,
  onToggleFolder,
  onRename,
  onDelete,
  onCreateIn,
  onMove,
  dragState,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  docs,
}: {
  node: TreeNode;
  level: number;
  selectedId: string | null;
  selectedFolderId: string | null;
  onSelect: (doc: GlobalDocument) => void;
  expandedFolders: Set<string>;
  onToggleFolder: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  onCreateIn: (parentId: string, isFolder: boolean) => void;
  onMove: (nodeId: string, newParentId: string | null) => void;
  dragState: { draggingId: string | null; dropTargetId: string | null };
  onDragStart: (id: string) => void;
  onDragOver: (e: React.DragEvent, id: string, isFolder: boolean) => void;
  onDragLeave: () => void;
  onDrop: (
    e: React.DragEvent,
    targetId: string | null,
    isFolder: boolean,
  ) => void;
  onDragEnd: () => void;
  docs: GlobalDocument[];
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(node.title);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isExpanded = expandedFolders.has(node.id);
  const isSelected = node.is_folder
    ? selectedFolderId === node.id
    : selectedId === node.id;
  const isDragging = dragState.draggingId === node.id;
  const isDropTarget = dragState.dropTargetId === node.id;

  useEffect(() => {
    setTitle(node.title);
  }, [node.title]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const handleRename = () => {
    const trimmed = title.trim();
    if (trimmed && trimmed !== node.title) {
      onRename(node.id, trimmed);
    } else {
      setTitle(node.title);
    }
    setEditing(false);
  };

  return (
    <div>
      <div
        className={cn(
          "group flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer text-sm hover:bg-accent/50 select-none",
          isSelected && "bg-accent text-accent-foreground",
          isDragging && "opacity-40",
          isDropTarget &&
            node.is_folder &&
            "ring-2 ring-primary ring-inset bg-primary/10",
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        draggable={!editing}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", node.id);
          onDragStart(node.id);
        }}
        onDragOver={(e) => onDragOver(e, node.id, node.is_folder)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, node.id, node.is_folder)}
        onDragEnd={onDragEnd}
        onClick={() => {
          if (node.is_folder) {
            onToggleFolder(node.id);
          } else {
            onSelect(node);
          }
        }}
      >
        {node.is_folder ? (
          <button
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFolder(node.id);
            }}
          >
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform",
                isExpanded && "rotate-90",
              )}
            />
          </button>
        ) : (
          <span className="w-3.5" />
        )}

        {node.is_folder ? (
          isExpanded ? (
            <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
          )
        ) : (
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        )}

        {editing ? (
          <Input
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
              if (e.key === "Escape") {
                setTitle(node.title);
                setEditing(false);
              }
            }}
            className="h-6 text-sm flex-1 py-0"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="truncate flex-1">{node.title}</span>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5 mr-2" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setMoveDialogOpen(true)}>
              <MoveRight className="h-3.5 w-3.5 mr-2" />
              Move to...
            </DropdownMenuItem>
            {node.is_folder && (
              <>
                <DropdownMenuItem onClick={() => onCreateIn(node.id, false)}>
                  <FilePlus className="h-3.5 w-3.5 mr-2" />
                  New Document
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onCreateIn(node.id, true)}>
                  <FolderPlus className="h-3.5 w-3.5 mr-2" />
                  New Folder
                </DropdownMenuItem>
              </>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Delete
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Delete {node.is_folder ? "folder" : "document"}?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {node.is_folder
                      ? "This will permanently delete this folder and all its contents."
                      : "This will permanently delete this document."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(node.id)}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <MoveToDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        docs={docs}
        nodeId={node.id}
        onMove={onMove}
      />

      {node.is_folder && isExpanded && (
        <div>
          {node.children.length === 0 ? (
            <div
              className="text-xs text-muted-foreground italic px-2 py-1"
              style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}
            >
              Empty folder
            </div>
          ) : (
            node.children.map((child) => (
              <TreeItem
                key={child.id}
                node={child}
                level={level + 1}
                selectedId={selectedId}
                selectedFolderId={selectedFolderId}
                onSelect={onSelect}
                expandedFolders={expandedFolders}
                onToggleFolder={onToggleFolder}
                onRename={onRename}
                onDelete={onDelete}
                onCreateIn={onCreateIn}
                onMove={onMove}
                dragState={dragState}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
                docs={docs}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──

export default function GlobalDocsPage() {
  const { data: docs = [], isLoading } = useGlobalDocuments();
  const createDoc = useCreateGlobalDocument();
  const updateDoc = useUpdateGlobalDocument();
  const deleteDoc = useDeleteGlobalDocument();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("doc");

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );

  // Track the currently selected folder for "create in" context
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // Drag-and-drop state
  const [dragState, setDragState] = useState<{
    draggingId: string | null;
    dropTargetId: string | null;
  }>({ draggingId: null, dropTargetId: null });

  const [search, setSearch] = useState("");

  const editorRef = useRef<CollaborativeEditorRef>(null);

  const tree = useMemo(() => buildTree(docs), [docs]);

  // Filter tree: match docs by title, include ancestor folders
  const filteredTree = useMemo(() => {
    if (!search.trim()) return tree;
    const q = search.toLowerCase();
    const matchingIds = new Set(
      docs
        .filter((d) => !d.is_folder && d.title.toLowerCase().includes(q))
        .map((d) => d.id),
    );
    const includedIds = new Set(matchingIds);
    for (const id of matchingIds) {
      let current = docs.find((d) => d.id === id);
      while (current?.parent_id) {
        includedIds.add(current.parent_id);
        current = docs.find((d) => d.id === current!.parent_id);
      }
    }
    return buildTree(docs.filter((d) => includedIds.has(d.id)));
  }, [tree, docs, search]);

  // Collect all folder IDs in the filtered tree for auto-expand during search
  const searchExpandedFolders = useMemo(() => {
    if (!search.trim()) return null;
    return new Set(docs.filter((d) => d.is_folder).map((d) => d.id));
  }, [search, docs]);

  const effectiveExpandedFolders = searchExpandedFolders ?? expandedFolders;

  const selectedDoc = useMemo(
    () => docs.find((d) => d.id === selectedId && !d.is_folder) ?? null,
    [docs, selectedId],
  );

  // Auto-expand parent folders when selecting a doc
  useEffect(() => {
    if (!selectedId) return;
    const parents = new Set<string>();
    const findParents = (id: string) => {
      const doc = docs.find((d) => d.id === id);
      if (doc?.parent_id) {
        parents.add(doc.parent_id);
        findParents(doc.parent_id);
      }
    };
    findParents(selectedId);
    if (parents.size > 0) {
      setExpandedFolders((prev) => {
        const next = new Set(prev);
        parents.forEach((p) => next.add(p));
        return next;
      });
    }
  }, [selectedId, docs]);

  const selectDoc = (doc: GlobalDocument) => {
    if (!doc.is_folder) {
      setSelectedFolderId(doc.parent_id);
      setSearchParams({ doc: doc.id });
    }
  };

  const toggleFolder = (id: string) => {
    setSelectedFolderId(id);
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = (parentId: string | null, isFolder: boolean) => {
    createDoc.mutate(
      {
        title: isFolder ? "New Folder" : "Untitled Document",
        parent_id: parentId,
        is_folder: isFolder,
      },
      {
        onSuccess: (doc) => {
          if (parentId) {
            setExpandedFolders((prev) => new Set(prev).add(parentId));
          }
          if (!isFolder) {
            setSearchParams({ doc: doc.id });
          }
        },
      },
    );
  };

  const handleRename = (id: string, title: string) => {
    updateDoc.mutate({ id, title });
  };

  const handleDelete = (id: string) => {
    if (selectedId === id) {
      setSearchParams({});
    }
    deleteDoc.mutate(id);
  };

  const handleMove = useCallback(
    (nodeId: string, newParentId: string | null) => {
      const node = docs.find((d) => d.id === nodeId);
      if (!node) return;
      // Don't move to same parent
      if ((node.parent_id ?? null) === newParentId) return;
      // Don't move into self or descendant
      if (newParentId && isDescendant(docs, nodeId, newParentId)) return;

      updateDoc.mutate({ id: nodeId, parent_id: newParentId });

      // Auto-expand target folder
      if (newParentId) {
        setExpandedFolders((prev) => new Set(prev).add(newParentId));
      }
    },
    [docs, updateDoc],
  );

  // ── Drag handlers ──

  const handleDragStart = useCallback((id: string) => {
    setDragState({ draggingId: id, dropTargetId: null });
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, targetId: string, isFolder: boolean) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dragState.draggingId || dragState.draggingId === targetId) return;
      // Only folders can be drop targets (or root)
      if (!isFolder) return;
      // Prevent dropping on own descendant
      if (isDescendant(docs, dragState.draggingId, targetId)) return;
      e.dataTransfer.dropEffect = "move";
      setDragState((prev) => ({ ...prev, dropTargetId: targetId }));
    },
    [dragState.draggingId, docs],
  );

  const handleDragLeave = useCallback(() => {
    setDragState((prev) => ({ ...prev, dropTargetId: null }));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetId: string | null, isFolder: boolean) => {
      e.preventDefault();
      e.stopPropagation();
      const draggedId = dragState.draggingId;
      setDragState({ draggingId: null, dropTargetId: null });

      if (!draggedId || draggedId === targetId) return;
      if (targetId && !isFolder) return;

      handleMove(draggedId, targetId);
    },
    [dragState.draggingId, handleMove],
  );

  const handleDragEnd = useCallback(() => {
    setDragState({ draggingId: null, dropTargetId: null });
  }, []);

  // Handle drop on root area (the scrollable tree container)
  const handleRootDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const draggedId = dragState.draggingId;
      setDragState({ draggingId: null, dropTargetId: null });
      if (!draggedId) return;
      handleMove(draggedId, null);
    },
    [dragState.draggingId, handleMove],
  );

  const handleRootDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <PageHeader title="Docs" />
        <Skeleton className="h-[calc(100vh-200px)] w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Docs" />
      <div className="flex-1 min-h-0">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Sidebar: folder tree */}
          <ResizablePanel defaultSize={25} minSize={15} maxSize={40}>
            <div className="flex flex-col h-full border-r">
              <div className="flex items-center gap-1 p-2 border-b">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => handleCreate(selectedFolderId, false)}
                >
                  <FilePlus className="h-3.5 w-3.5 mr-1" />
                  Doc
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => handleCreate(selectedFolderId, true)}
                >
                  <FolderPlus className="h-3.5 w-3.5 mr-1" />
                  Folder
                </Button>
                {selectedFolderId && (
                  <span className="text-xs text-muted-foreground truncate ml-1">
                    in{" "}
                    {docs.find((d) => d.id === selectedFolderId)?.title ??
                      "folder"}
                  </span>
                )}
              </div>
              <div className="px-2 py-1.5 border-b">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search pages..."
                />
              </div>
              <div
                className="flex-1 overflow-y-auto py-1"
                onDragOver={handleRootDragOver}
                onDrop={handleRootDrop}
              >
                {filteredTree.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <FileText className="h-10 w-10 text-muted-foreground/50 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {search
                        ? "No pages match your search."
                        : "No documents yet"}
                    </p>
                    {!search && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Create a document or folder to get started.
                      </p>
                    )}
                  </div>
                ) : (
                  filteredTree.map((node) => (
                    <TreeItem
                      key={node.id}
                      node={node}
                      level={0}
                      selectedId={selectedId}
                      selectedFolderId={selectedFolderId}
                      onSelect={selectDoc}
                      expandedFolders={effectiveExpandedFolders}
                      onToggleFolder={toggleFolder}
                      onRename={handleRename}
                      onDelete={handleDelete}
                      onCreateIn={handleCreate}
                      onMove={handleMove}
                      dragState={dragState}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                      docs={docs}
                    />
                  ))
                )}
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Main content: document editor */}
          <ResizablePanel defaultSize={75} minSize={40}>
            <div className="h-full overflow-y-auto">
              {selectedDoc ? (
                <div className="p-6 space-y-4">
                  <DocumentEditor doc={selectedDoc} editorRef={editorRef} />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <FileText className="h-16 w-16 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">
                    Select a document to start editing
                  </p>
                </div>
              )}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
}

// ── Document Editor ──

function DocumentEditor({
  doc,
  editorRef,
}: {
  doc: GlobalDocument;
  editorRef: React.RefObject<CollaborativeEditorRef | null>;
}) {
  const updateDoc = useUpdateGlobalDocument();
  const [title, setTitle] = useState(doc.title);

  useEffect(() => {
    setTitle(doc.title);
  }, [doc.title]);

  const handleTitleBlur = () => {
    const trimmed = title.trim();
    if (trimmed && trimmed !== doc.title) {
      updateDoc.mutate({ id: doc.id, title: trimmed });
    }
  };

  return (
    <>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={handleTitleBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="text-2xl font-bold border-transparent hover:border-input focus:border-input transition-colors h-auto py-2"
        placeholder="Document title"
      />
      <CollaborativeEditor
        ref={editorRef}
        documentId={doc.document_id}
        editable={true}
        placeholder="Start writing..."
      />
    </>
  );
}
